import React, { useState, useEffect, useCallback } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader";
import Alert from '../common/Alert';
import vehicleService from '../../services/vehicleService.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import { Truck, ArrowLeft } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import VehicleOwnerAssignmentFields, { EMPTY_NEW_OWNER } from './VehicleOwnerAssignmentFields.js';
import VehicleLocationPointFields from './VehicleLocationPointFields.js';
import { mergeCanAccess } from '../../utils/companyFeatures.js';
import {
  extractLocationIdsFromPoints,
  validateLocationPointSelection,
} from '../../utils/vehicleLocationPoints.js';
import { useAuth } from '../../context/AuthContext';
import { VEHICLE_TYPE_OPTIONS } from '../../utils/vehicleTypes.js';
import {

  syncVehicleOwnerAssignment,
  validateVehicleOwnerAssignment,
  findOwnerIdForVehicle,
} from '../../utils/vehicleOwnerAssign.js';

export default function EditVehicle() {
  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const { user } = useAuth();
  const companyAccess = mergeCanAccess(user?.company_features || user?.company_can_access || {});
  const hasCommission = Boolean(companyAccess.commission);

  const [loading, setLoading] = useState(true);
  const [vehicleOwners, setVehicleOwners] = useState([]);
  const [initialOwnerId, setInitialOwnerId] = useState('');
  const [ownerMode, setOwnerMode] = useState('none');
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [newOwnerData, setNewOwnerData] = useState({ ...EMPTY_NEW_OWNER });
  const [ownerPercentageOverride, setOwnerPercentageOverride] = useState('');
  const [pickupLocationIds, setPickupLocationIds] = useState([]);
  const [dropLocationIds, setDropLocationIds] = useState([]);
  const [initialPickupLocationIds, setInitialPickupLocationIds] = useState([]);
  const [initialDropLocationIds, setInitialDropLocationIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  const [formData, setFormData] = useState({
    vehicle_name: '',
    vehicle_number: '',
    vehicle_type: 'cargo_goods',
    status: 'active',
    vehicle_size: '',
    capacity: '',
    vehicle_contract_amount: ''
  });

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    vehicle_name:            () => '',
    vehicle_number:          (v) => validators.required(v, 'Vehicle number'),
    vehicle_size:            (v) => v ? validators.number(v, { min: 0, label: 'Vehicle size' }) : '',
    capacity:                (v) => v ? validators.number(v, { min: 0, label: 'Capacity' }) : '',
    vehicle_contract_amount: (v) => validators.number(v, { min: 0, label: 'Contract amount' }),
  };

  const getFieldValidationError = (field, value, rules = validationRules) => {
    const ruleFn = rules[field];
    if (!ruleFn) return '';
    const fieldRules = Array.isArray(ruleFn) ? ruleFn : [ruleFn];
    return fieldRules.reduce((err, fn) => err || fn(value), '');
  };

  const vehicleTypes = VEHICLE_TYPE_OPTIONS;

  const vehicleStatuses = [
    { value: 'active', label: 'ACTIVE' },
    { value: 'inactive', label: 'INACTIVE' }
  ];

  const fetchVehicle = useCallback(async () => {
    try {
      const response = await vehicleService.getVehicle(vehicleId);
      const vehicle = response.data.data;
      const normalizeLoadedAmount = (value) => {
        if (value === null || value === undefined || value === '') return '';
        return String(value);
      };

      const vehicleData = {
        vehicle_name: vehicle.vehicle_name ?? '',
        vehicle_number: vehicle.vehicle_number ?? '',
        vehicle_type: vehicle.vehicle_type,
        status: vehicle.status,
        vehicle_size: normalizeLoadedAmount(vehicle.vehicle_size),
        capacity: normalizeLoadedAmount(vehicle.capacity),
        vehicle_contract_amount: normalizeLoadedAmount(vehicle.vehicle_contract_amount),
      };
      setFormData(vehicleData);
      setOriginalData(vehicleData);

      const pickupIds = extractLocationIdsFromPoints(vehicle.pickup_point);
      const dropIds = extractLocationIdsFromPoints(vehicle.drop_point);
      setPickupLocationIds(pickupIds);
      setDropLocationIds(dropIds);
      setInitialPickupLocationIds(pickupIds);
      setInitialDropLocationIds(dropIds);

      const ownersRes = await vehicleOwnerService.getAll(1, 500).catch(() => ({ data: [] }));
      const ownersList = ownersRes?.data || ownersRes || [];
      const owners = Array.isArray(ownersList) ? ownersList : [];
      setVehicleOwners(owners);
      const linkedOwnerId = findOwnerIdForVehicle(owners, vehicle.id || vehicleId);
      setInitialOwnerId(linkedOwnerId);
      if (linkedOwnerId) {
        setOwnerMode('existing');
        setSelectedOwnerId(linkedOwnerId);
      } else {
        setOwnerMode('none');
        setSelectedOwnerId('');
      }
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to fetch vehicle');
      navigate('/vehicles');
    } finally {
      setLoading(false);
    }
  }, [vehicleId, navigate]);

  useEffect(() => {
    fetchVehicle();
  }, [fetchVehicle]);

  const hasChanges = () => {
    if (!originalData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData) ||
          JSON.stringify(pickupLocationIds) !== JSON.stringify(initialPickupLocationIds) ||
          JSON.stringify(dropLocationIds) !== JSON.stringify(initialDropLocationIds);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'vehicle_contract_amount' || name === 'vehicle_size' || name === 'capacity') filtered = inputFilters.amount(value);
    else if (name === 'vehicle_number') filtered = value.toUpperCase();

    setFormData(prev => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      const failedField = Object.keys(validationRules).find(
        (field) => getFieldValidationError(field, formData[field])
      );
      const errorMessage = failedField
        ? getFieldValidationError(failedField, formData[failedField])
        : 'Please fix the highlighted errors before submitting';
      Alert.error(errorMessage);
      return;
    }

    if (!hasChanges()) {
      Alert.info('No changes detected');
      return;
    }

    const confirmed = await Alert.confirm(
      'Update vehicle information?',
      'Confirm Update'
    );

    if (!confirmed) return;

    const ownerError = validateVehicleOwnerAssignment({
      ownerMode,
      selectedOwnerId,
      newOwnerData,
      ownerPercentageOverride,
      vehicleOwners,
      hasCommission,
    });
    if (ownerError) {
      Alert.error(ownerError);
      return;
    }

    const locationError = validateLocationPointSelection(pickupLocationIds, dropLocationIds);
    if (locationError) {
      Alert.error(locationError);
      return;
    }

    try {
      setSubmitting(true);

      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        // Send empty string for cleared size/capacity so backend can null them out
        if (['vehicle_size', 'capacity'].includes(key)) {
          formDataToSend.append(key, formData[key] ?? '');
        } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      formDataToSend.append(
        'pickup_point',
        JSON.stringify(pickupLocationIds.map((locationId) => ({ location_id: locationId })))
      );
      formDataToSend.append(
        'drop_point',
        JSON.stringify(dropLocationIds.map((locationId) => ({ location_id: locationId })))
      );

      await vehicleService.updateVehicle(vehicleId, formDataToSend);

      await syncVehicleOwnerAssignment({
        vehicleId,
        ownerMode,
        selectedOwnerId,
        vehicleOwners,
        newOwnerData,
        ownerPercentageOverride,
        previousOwnerId: initialOwnerId,
      });

      Alert.success('Vehicle updated successfully');
      setTimeout(() => navigate('/vehicles'), 1000);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to update vehicle');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (hasChanges()) {
      const confirmed = await Alert.confirm(
        'You have unsaved changes. Are you sure you want to cancel?',
        'Unsaved Changes'
      );
      if (!confirmed) return;
    }

    navigate('/vehicles');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading vehicle details..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
            <Truck className="w-6 h-6 text-secondary shrink-0" />Edit Vehicle
          </h1>
          <button
            onClick={handleCancel}
            className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt hover:bg-buttonBg border border-current rounded-xl transition-all duration-300 font-medium whitespace-nowrap shrink-0"
          >
            <span className="group-hover:-translate-x-1 transition-transform">
              <ArrowLeft className="w-4 h-4" />
            </span>
            Back to Vehicles
          </button>
        </div>

        {/* Change Indicator */}
        {hasChanges() && (
          <div className="mb-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-orange-500 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-orange-800">
                You have unsaved changes
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 space-y-6 sm:space-y-8">
          {/* Vehicle Information */}
          <div>
            <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Vehicle Information
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-6">
              {/* Vehicle Name */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Vehicle Name
                </label>
                <input
                  type="text"
                  name="vehicle_name"
                  value={formData.vehicle_name}
                  onChange={handleChange}
                  placeholder="Enter vehicle name"
                  className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.vehicle_name ? 'border-red-400' : 'border-gray-300'}`}
                />
                <ValidationMessage error={errors.vehicle_name} />
              </div>

              {/* Vehicle Number */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Vehicle Number
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="vehicle_number"
                  value={formData.vehicle_number}
                  onChange={handleChange}
                  placeholder="Enter vehicle number (e.g., MH12AB1234)"
                  required
                  className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.vehicle_number ? 'border-red-400' : 'border-gray-300'}`}
                />
                <ValidationMessage error={errors.vehicle_number} />
              </div>

              {/* Vehicle Type */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Vehicle Type
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <SelectField variant="form"
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                >
                  {vehicleTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </SelectField>
              </div>

              {/* Vehicle Size (Ft) */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Vehicle Size (Ft)
                </label>
                <input
                  type="text"
                  name="vehicle_size"
                  value={formData.vehicle_size}
                  onChange={handleChange}
                  placeholder="e.g., 20"
                  className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.vehicle_size ? 'border-red-400' : 'border-gray-300'}`}
                />
                <ValidationMessage error={errors.vehicle_size} />
              </div>

              {/* Capacity (MT) */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Capacity (MT)
                </label>
                <input
                  type="text"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  placeholder="e.g., 9"
                  className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.capacity ? 'border-red-400' : 'border-gray-300'}`}
                />
                <ValidationMessage error={errors.capacity} />
                <p className="text-xs text-gray-500 mt-1">Used to warn about overweight loads during ML creation.</p>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Status
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <SelectField variant="form"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                >
                  {vehicleStatuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </SelectField>
              </div>

              {/* Vehicle Contract Amount */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Contract Amount (₹)
                </label>
                <input
                  type="text"
                  name="vehicle_contract_amount"
                  value={formData.vehicle_contract_amount}
                  onChange={handleChange}
                  placeholder="Enter contract amount"
                  className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.vehicle_contract_amount ? 'border-red-400' : 'border-gray-300'}`}
                />
                <ValidationMessage error={errors.vehicle_contract_amount} />
              </div>
            </div>

            <VehicleLocationPointFields
              pickupLocationIds={pickupLocationIds}
              setPickupLocationIds={setPickupLocationIds}
              dropLocationIds={dropLocationIds}
              setDropLocationIds={setDropLocationIds}
            />

            <VehicleOwnerAssignmentFields
              ownerMode={ownerMode}
              setOwnerMode={setOwnerMode}
              selectedOwnerId={selectedOwnerId}
              setSelectedOwnerId={setSelectedOwnerId}
              newOwnerData={newOwnerData}
              setNewOwnerData={setNewOwnerData}
              ownerPercentageOverride={ownerPercentageOverride}
              setOwnerPercentageOverride={setOwnerPercentageOverride}
              initialOwnerId={initialOwnerId}
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="primary" type="submit" disabled={submitting || !hasChanges()} loading={submitting}>
              {submitting ? 'Updating Vehicle...' : 'Update Vehicle'}
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
